import { AlertTriangle } from "lucide-react";
import Link from "next/link";
import React from "react";

import Hyperlink from "@/components/shared/hyperlink/hyperlink";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export interface NonCandidateModalViewProps {
  adminDashboardHref: string;
  onCreateCandidateAccount: () => Promise<void>;
  onSignOut: () => Promise<void>;
}

export function NonCandidateModalView({
  adminDashboardHref,
  onCreateCandidateAccount,
  onSignOut,
}: NonCandidateModalViewProps) {
  return (
    <Dialog open>
      <DialogContent showCloseButton={false}>
        <DialogHeader className="flex flex-col items-center text-center gap-4">
          <div className="h-12 w-12 rounded-full bg-warning-soft flex items-center justify-center">
            <AlertTriangle className="h-6 w-6 text-warning" />
          </div>
          <div className="space-y-2 text-center">
            <DialogTitle className="text-xl font-bold text-foreground mb-5">
              You&apos;re Signed In as an Admin
            </DialogTitle>
            <DialogDescription className="text-base text-ink-subtle">
              This job board is for candidates looking for teaching positions.
              You&apos;re currently signed into an admin account, which
              doesn&apos;t have access to candidate features like job search or
              applications.
            </DialogDescription>
          </div>
        </DialogHeader>

        <div className="text-ink-subtle text-left space-y-2">
          <p className="font-medium text-foreground">
            What would you like to do?
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>
              <span className="font-medium text-foreground">
                Browse jobs as a candidate
              </span>
              {" - sign out or create a separate candidate account using a "}
              personal email
            </li>
            <li>
              <span className="font-medium text-foreground">
                Manage your district&apos;s roles
              </span>{" "}
              <Hyperlink className="underline" href={adminDashboardHref}>
                go to your admin dashboard
              </Hyperlink>
            </li>
          </ul>
        </div>

        <div className="flex flex-col gap-3">
          <Button onClick={onCreateCandidateAccount} size="lg">
            Create a Candidate Account
          </Button>

          <Button asChild variant="outline" size="lg">
            <Link href={adminDashboardHref}>Go to Admin Dashboard</Link>
          </Button>

          <Button variant="ghost" size="lg" onClick={onSignOut}>
            Sign Out
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
